import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { sensorService } from '../services/sensorService';
import { SensorReading } from '../types';
import { getStoredTempUnit, convertTemperature, tempUnitLabel } from '../utils/temperatureUtils';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

interface SE0XReadingsChartProps {
  sensorId: number;
  latestReading?: SensorReading | null;
  onNewReading?: (reading: SensorReading) => void;
}

type DateRangePreset = '1d' | '7d' | '30d' | '90d' | 'custom';

/** Detect which probes (1-4) have data in a set of readings */
function detectActiveProbes(readings: SensorReading[]): number[] {
  const probes: number[] = [];
  for (let p = 1; p <= 4; p++) {
    const tempKey = `temp_soil${p}` as keyof SensorReading;
    const waterKey = `water_soil${p}` as keyof SensorReading;
    const conductKey = `conduct_soil${p}` as keyof SensorReading;
    const hasData = readings.some(
      r => r[tempKey] != null || r[waterKey] != null || r[conductKey] != null
    );
    if (hasData) {
      probes.push(p);
    }
  }
  return probes;
}

const SE0XReadingsChart: React.FC<SE0XReadingsChartProps> = ({ sensorId, latestReading, onNewReading }) => {
  const { t } = useTranslation();
  const [readings, setReadings] = useState<SensorReading[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Per-probe toggle states
  const [showTemperature, setShowTemperature] = useState(true);
  const [showMoisture, setShowMoisture] = useState(true);
  const [showEC, setShowEC] = useState(true);

  // Date range states
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('1d');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Polling refs
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastReadingIdRef = useRef<number | null>(null);
  const isPollingInProgressRef = useRef<boolean>(false);

  // Calculate date range based on preset
  const getDateRange = useCallback(() => {
    const end = new Date();
    let start = new Date();

    if (dateRangePreset === 'custom' && startDate && endDate) {
      return {
        start: new Date(startDate),
        end: new Date(endDate),
      };
    }

    switch (dateRangePreset) {
      case '1d':
        start.setDate(start.getDate() - 1);
        break;
      case '7d':
        start.setDate(start.getDate() - 7);
        break;
      case '30d':
        start.setDate(start.getDate() - 30);
        break;
      case '90d':
        start.setDate(start.getDate() - 90);
        break;
    }

    return { start, end };
  }, [dateRangePreset, startDate, endDate]);

  // Determine time unit based on date range
  const getTimeUnit = useCallback(() => {
    const { start, end } = getDateRange();
    const diffMs = end.getTime() - start.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);

    if (diffDays <= 1) return 'hour';
    if (diffDays <= 7) return 'day';
    if (diffDays <= 60) return 'day';
    return 'week';
  }, [getDateRange]);

  // Load readings
  const loadReadings = useCallback(async (isPolling = false) => {
    if (isPolling && isPollingInProgressRef.current) {
      return;
    }

    if (isPolling) {
      isPollingInProgressRef.current = true;
    } else {
      setLoading(true);
    }

    try {
      const { start, end } = getDateRange();
      const data = await sensorService.getReadings(sensorId, {
        start_time: start.toISOString(),
        end_time: end.toISOString(),
        limit: 5000,
      });

      const sortedData = data.sort((a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );

      setReadings(sortedData);

      if (isPolling && sortedData.length > 0) {
        const newestReading = sortedData[sortedData.length - 1];
        if (lastReadingIdRef.current !== null &&
          newestReading.id !== lastReadingIdRef.current) {
          if (onNewReading) {
            onNewReading(newestReading);
          }
        }
        lastReadingIdRef.current = newestReading.id;
      } else if (!isPolling && sortedData.length > 0) {
        lastReadingIdRef.current = sortedData[sortedData.length - 1].id;
      }

      setError(null);
    } catch (err: any) {
      if (!isPolling) {
        setError(err.response?.data?.detail || 'Failed to load readings');
      }
    } finally {
      if (isPolling) {
        isPollingInProgressRef.current = false;
      } else {
        setLoading(false);
      }
    }
  }, [sensorId, getDateRange, onNewReading]);

  // Initial load and when date range changes
  useEffect(() => {
    loadReadings();
  }, [loadReadings]);

  // Set up polling for real-time updates
  useEffect(() => {
    pollingIntervalRef.current = setInterval(() => {
      loadReadings(true);
    }, 30000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [loadReadings]);

  // Update chart when latestReading changes from parent
  useEffect(() => {
    if (latestReading) {
      setReadings(prev => {
        const exists = prev.some(r => r.id === latestReading.id);
        if (!exists) {
          const updated = [...prev, latestReading].sort((a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );
          return updated;
        }
        return prev;
      });
    }
  }, [latestReading]);

  const activeProbes = detectActiveProbes(readings);

  const handlePresetChange = (preset: DateRangePreset) => {
    setDateRangePreset(preset);
    if (preset !== 'custom') {
      setStartDate('');
      setEndDate('');
    }
  };

  const handleCustomDateChange = () => {
    if (startDate && endDate) {
      setDateRangePreset('custom');
    }
  };

  /** Build chart data and options for a single probe */
  const buildProbeChart = (probeNum: number) => {
    const tempUnit = getStoredTempUnit();
    const unitLabel = tempUnitLabel(tempUnit);
    const tempKey = `temp_soil${probeNum}` as keyof SensorReading;
    const waterKey = `water_soil${probeNum}` as keyof SensorReading;
    const conductKey = `conduct_soil${probeNum}` as keyof SensorReading;

    const chartData = {
      labels: readings.map(r => new Date(r.timestamp)),
      datasets: [
        ...(showTemperature ? [{
          label: `Temperature (${unitLabel})`,
          data: readings.map(r => convertTemperature(r[tempKey] as number | null, tempUnit)),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.5)',
          borderWidth: 2,
          pointRadius: readings.length > 100 ? 0 : 3,
          tension: 0.1,
          yAxisID: 'y',
        }] : []),
        ...(showMoisture ? [{
          label: `Soil Moisture (%)`,
          data: readings.map(r => r[waterKey] as number | null),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.5)',
          borderWidth: 2,
          pointRadius: readings.length > 100 ? 0 : 3,
          tension: 0.1,
          yAxisID: 'y1',
        }] : []),
        ...(showEC ? [{
          label: `EC (mS/cm)`,
          data: readings.map(r => (r[conductKey] as number | null) != null ? (r[conductKey] as number) / 1000 : null),
          borderColor: 'rgb(168, 85, 247)',
          backgroundColor: 'rgba(168, 85, 247, 0.5)',
          borderWidth: 2,
          pointRadius: readings.length > 100 ? 0 : 3,
          tension: 0.1,
          yAxisID: 'y2',
        }] : []),
      ],
    };

    const chartOptions: ChartOptions<'line'> = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          position: 'top',
        },
        title: {
          display: true,
          text: `Probe ${probeNum}`,
        },
        tooltip: {
          callbacks: {
            title: (items) => {
              if (items.length > 0 && items[0].parsed.x != null) {
                const date = new Date(items[0].parsed.x);
                return date.toLocaleString();
              }
              return '';
            },
          },
        },
      },
      scales: {
        x: {
          type: 'time',
          time: {
            unit: getTimeUnit(),
            displayFormats: {
              hour: 'HH:mm',
              day: 'MMM d',
              week: 'MMM d',
            },
          },
          title: {
            display: true,
            text: 'Time',
          },
        },
        y: {
          type: 'linear',
          display: showTemperature,
          position: 'left',
          title: {
            display: true,
            text: `Temperature (${unitLabel})`,
            color: 'rgb(59, 130, 246)',
          },
          ticks: {
            color: 'rgb(59, 130, 246)',
          },
        },
        y1: {
          type: 'linear',
          display: showMoisture,
          position: 'left',
          title: {
            display: true,
            text: 'Soil Moisture (%)',
            color: 'rgb(34, 197, 94)',
          },
          ticks: {
            color: 'rgb(34, 197, 94)',
          },
          grid: {
            drawOnChartArea: false,
          },
        },
        y2: {
          type: 'linear',
          display: showEC,
          position: 'right',
          title: {
            display: true,
            text: 'EC (mS/cm)',
            color: 'rgb(168, 85, 247)',
          },
          ticks: {
            color: 'rgb(168, 85, 247)',
          },
          grid: {
            drawOnChartArea: false,
          },
        },
      },
    };

    return { chartData, chartOptions };
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      {/* Header */}
      <div className="mb-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Toggle buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowTemperature(!showTemperature)}
              className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                showTemperature
                  ? 'bg-blue-500 text-white border-blue-500'
                  : 'bg-white text-blue-500 border-blue-500 hover:bg-blue-50'
              }`}
            >
              {t('dashboard.temperature')}
            </button>
            <button
              onClick={() => setShowMoisture(!showMoisture)}
              className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                showMoisture
                  ? 'bg-green-500 text-white border-green-500'
                  : 'bg-white text-green-500 border-green-500 hover:bg-green-50'
              }`}
            >
              {t('sensorDetail.moisture')}
            </button>
            <button
              onClick={() => setShowEC(!showEC)}
              className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                showEC
                  ? 'bg-purple-500 text-white border-purple-500'
                  : 'bg-white text-purple-500 border-purple-500 hover:bg-purple-50'
              }`}
            >
              {t('common.ec')}
            </button>
          </div>

          {/* Date range preset buttons */}
          <div className="flex flex-wrap gap-2">
            {(['1d', '7d', '30d', '90d'] as DateRangePreset[]).map((preset) => (
              <button
                key={preset}
                onClick={() => handlePresetChange(preset)}
                className={`px-3 py-1 text-sm rounded border transition-colors ${
                  dateRangePreset === preset
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {preset === '1d' ? t('chart.hours24') : preset === '7d' ? t('chart.days7') : preset === '30d' ? t('chart.days30') : t('chart.days90')}
              </button>
            ))}
          </div>
        </div>

        {/* Custom date range */}
        <div className="mt-4 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('chart.startDate')}</label>
            <input
              type="datetime-local"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('chart.endDate')}</label>
            <input
              type="datetime-local"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <button
            onClick={handleCustomDateChange}
            disabled={!startDate || !endDate}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('chart.applyCustomRange')}
          </button>
          <button
            onClick={() => loadReadings()}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            {t('chart.refresh')}
          </button>
        </div>
      </div>

      {/* Active probes summary */}
      {!loading && activeProbes.length > 0 && (
        <div className="mb-4 flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">{t('chart.activeProbes')}</span>
          {activeProbes.map(p => (
            <span
              key={p}
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
            >
              {t('sensorDetail.probe')} {p}
            </span>
          ))}
        </div>
      )}

      {/* Loading / Error / Empty states */}
      {loading ? (
        <div className="flex items-center justify-center h-64 bg-white bg-opacity-75">
          <div className="text-gray-600">{t('chart.loadingReadings')}</div>
        </div>
      ) : error ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-red-600">{error}</div>
        </div>
      ) : readings.length === 0 ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">{t('chart.noReadingsForRange')}</div>
        </div>
      ) : activeProbes.length === 0 ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">{t('chart.noProbeData')}</div>
        </div>
      ) : (
        /* Render one chart per active probe */
        <div className="space-y-6">
          {activeProbes.map(probeNum => {
            const { chartData, chartOptions } = buildProbeChart(probeNum);
            return (
              <div key={probeNum}>
                <div className="relative" style={{ height: '350px' }}>
                  <Line data={chartData} options={chartOptions} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reading count */}
      {!loading && readings.length > 0 && (
        <div className="mt-4 text-sm text-gray-500 text-right">
          {t('chart.showingReading', { count: readings.length })}
          {dateRangePreset !== 'custom' && ` ${dateRangePreset === '1d' ? t('chart.fromLast24h') : dateRangePreset === '7d' ? t('chart.fromLast7d') : dateRangePreset === '30d' ? t('chart.fromLast30d') : t('chart.fromLast90d')}`}
        </div>
      )}
    </div>
  );
};

export default SE0XReadingsChart;
